import React, { useEffect, useRef } from "react";
import { CardHeader } from "reactstrap";
import Skeleton from "react-loading-skeleton";
import { Chart } from "chart.js/auto";
import domain from 'src/config/domain';
import {
  richTooltipConfig,
  richAnimationConfig,
  crosshairPlugin,
} from 'src/config/chart';

// Register the crosshair plugin
Chart.register(crosshairPlugin);

const ChartComponent = ({ data, loading }) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const domainConfig = domain();

  useEffect(() => {
    if (loading === false && canvasRef.current) {
      // Destroy previous chart if exists
      if (chartRef.current) {
        chartRef.current.destroy();
      }

      const ctx = canvasRef.current.getContext("2d");
      // Use a warning/danger color for declines to differentiate from positive metrics
      const gradient = ctx.createLinearGradient(0, 0, 0, 300);
      gradient.addColorStop(0, 'rgba(239, 68, 68, 0.25)');
      gradient.addColorStop(1, 'rgba(239, 68, 68, 0.0)');

      var config = {
        type: "line",
        data: {
          labels: data.map((item) => item.month),
          datasets: [
            {
              label: "Declines",
              fill: true,
              backgroundColor: gradient,
              borderColor: '#ef4444',
              borderWidth: 2.5,
              tension: 0.4,
              pointRadius: 4,
              pointBackgroundColor: '#fff',
              pointBorderColor: '#ef4444',
              pointBorderWidth: 2,
              pointHoverRadius: 8,
              pointHoverBackgroundColor: '#fff',
              pointHoverBorderColor: '#ef4444',
              pointHoverBorderWidth: 3,
              pointHitRadius: 20,
              data: data.map((item) => item.count),
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          hover: {
            mode: 'index',
            intersect: false,
          },
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              ...richTooltipConfig,
              callbacks: {
                label: function(context) {
                  const value = context.parsed.y;
                  return ` ${new Intl.NumberFormat('en-GB').format(value)} declined`;
                },
              },
            },
          },
          animation: richAnimationConfig,
          scales: {
            y: {
              display: true,
              grid: {
                display: true,
                color: 'rgba(148, 163, 184, 0.08)',
                drawBorder: false,
              },
              border: {
                display: false,
              },
              ticks: {
                color: '#94a3b8',
                font: {
                  size: 11,
                  family: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                },
                padding: 12,
                maxTicksLimit: 5,
              },
            },
            x: {
              display: true,
              grid: {
                display: false,
                drawBorder: false,
              },
              border: {
                display: false,
              },
              ticks: {
                color: '#94a3b8',
                font: {
                  size: 10,
                  family: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                },
                padding: 8,
                maxTicksLimit: 6,
                maxRotation: 0
              },
            },
          },
        },
      };

      chartRef.current = new Chart(ctx, config);
    }

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    }
  }, [loading, data]);

  return (
    <div className="flex grow flex-col">
        <CardHeader className="border-bottom bg-gray-50 mb-2">
          <h2 className={`text-xl font-normal ${domainConfig.styles.textColour}`}>
            Declines <span className="font-bold">By Month</span>
          </h2>
        </CardHeader>
      {loading && (
        <div className="text-center px-6 py-3">
          <Skeleton count={1} height="260px" />
        </div>
      )}
      {!loading && (
        <div className="py-4 px-4 grow">
          <canvas ref={canvasRef}></canvas>
        </div>
      )}

    </div>
  );
};
export default ChartComponent;
